<script>
import { useAlert } from 'dashboard/composables';
import inboxMixin from 'shared/mixins/inboxMixin';
import ImapSettings from '../ImapSettings.vue';
import SmtpSettings from '../SmtpSettings.vue';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import WhatsappReauthorize from '../channels/whatsapp/Reauthorize.vue';
import InboxesAPI from 'dashboard/api/inboxes';

export default {
  components: { ImapSettings, SmtpSettings, WhatsappReauthorize },
  mixins: [inboxMixin],
  props: {
    inbox: { type: Object, default: () => ({}) },
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      hmacMandatory: false,
      whatsAppInboxAPIKey: '',
      isRequestingReauthorization: false,
      isSyncingTemplates: false,
      isRefreshingLineToken: false,
      liffEnabled: false,
      liffId: '',
      liffPageUrl: '',
      botBasicId: '',
      copiedKey: '',
      // Green API status
      greenApiStatus: null,
      isLoadingGreenApiStatus: false,
      isReconnecting: false,
      greenApiQrCode: null,
      greenApiQrCodeUrl: null,
      greenApiStatusPollTimer: null,
    };
  },
  validations: {
    whatsAppInboxAPIKey: { required },
  },
  computed: {
    isGreenApiChannel() {
      return (
        this.isAWhatsAppChannel &&
        this.inbox.provider_config?.instance_id !== undefined &&
        !this.inbox.provider_config?.webhook_verify_token &&
        !this.inbox.provider_config?.api_key
      );
    },
    greenApiConnected() {
      return this.greenApiStatus?.connected === true;
    },
    greenApiStatusDotClass() {
      if (this.isLoadingGreenApiStatus) return 'cfg-status-dot--grey';
      return this.greenApiConnected ? 'cfg-status-dot--green' : 'cfg-status-dot--red';
    },
    greenApiStatusText() {
      if (this.isLoadingGreenApiStatus) return 'Checking...';
      return this.greenApiConnected ? 'Connected' : 'Not Connected';
    },
    greenApiPhoneNumber() {
      return this.greenApiStatus?.phone_number || this.inbox.provider_config?.phone_number || '—';
    },
    greenApiInstanceId() {
      return this.greenApiStatus?.instance_id || this.inbox.provider_config?.instance_id || '—';
    },
    greenApiLastAuthorized() {
      const ts = this.greenApiStatus?.last_authorized_at;
      if (!ts) return '—';
      return this.formatTimeAgo(new Date(ts));
    },
    isEmbeddedSignupWhatsApp() {
      return this.inbox.provider_config?.source === 'embedded_signup';
    },
    whatsappAppId() {
      return window.chatwootConfig?.whatsappAppId;
    },
    isLineShortLivedMode() {
      return this.inbox.token_mode === 'short_lived';
    },
    lineTokenStatus() {
      if (!this.inbox.token_mode) return null;
      if (this.inbox.token_mode === 'long_lived') return 'active_manual';
      if (!this.inbox.token_expires_at) return 'active_auto';
      const expiresAt = new Date(this.inbox.token_expires_at);
      const daysUntilExpiry = Math.ceil((expiresAt - new Date()) / 864e5);
      if (daysUntilExpiry <= 0) return 'expired';
      if (daysUntilExpiry <= 5) return 'expiring_soon';
      return 'active_auto';
    },
    lineTokenStatusText() {
      const statusMap = {
        active_manual: this.$t('INBOX_MGMT.ADD.LINE_CHANNEL.TOKEN_STATUS.ACTIVE_MANUAL'),
        active_auto: this.$t('INBOX_MGMT.ADD.LINE_CHANNEL.TOKEN_STATUS.ACTIVE_AUTO'),
        expiring_soon: this.$t('INBOX_MGMT.ADD.LINE_CHANNEL.TOKEN_STATUS.EXPIRING_SOON', { days: this.daysUntilLineTokenExpiry }),
        expired: this.$t('INBOX_MGMT.ADD.LINE_CHANNEL.TOKEN_STATUS.EXPIRED'),
      };
      return statusMap[this.lineTokenStatus] || '';
    },
    lineTokenStatusSubtitle() {
      return this.isLineShortLivedMode
        ? 'Token is auto-managed by the system (30-day validity)'
        : 'Manual token (no expiration)';
    },
    lineTokenExpiresIn() {
      if (!this.isLineShortLivedMode || !this.inbox.token_expires_at) return null;
      const days = this.daysUntilLineTokenExpiry;
      if (days <= 0) return null;
      return this.$t('INBOX_MGMT.ADD.LINE_CHANNEL.TOKEN_STATUS.EXPIRES_IN', { days });
    },
    lineTokenLastRefreshed() {
      if (!this.isLineShortLivedMode || !this.inbox.token_last_refreshed_at) return null;
      return this.$t('INBOX_MGMT.ADD.LINE_CHANNEL.TOKEN_STATUS.LAST_REFRESHED', {
        time: this.formatTimeAgo(new Date(this.inbox.token_last_refreshed_at)),
      });
    },
    daysUntilLineTokenExpiry() {
      if (!this.inbox.token_expires_at) return 0;
      return Math.ceil((new Date(this.inbox.token_expires_at) - new Date()) / 864e5);
    },
    liffConfig() { return this.inbox.liff_config || {}; },
    liffEnabledText() {
      return this.liffEnabled
        ? this.$t('INBOX_MGMT.ADD.LINE_CHANNEL.LIFF_LINKING.ENABLED')
        : this.$t('INBOX_MGMT.ADD.LINE_CHANNEL.LIFF_LINKING.DISABLED');
    },
    baseHostUrl() {
      return (window.wlabConfig?.hostURL || window.chatwootConfig?.hostURL || `https://${window.location.host}`).replace(/\/$/, '');
    },
    liffCheckinUrl() {
      return `${this.baseHostUrl}/public/api/v1/line/checkins/${this.inbox.line_channel_id || ''}`;
    },
    webWidgetEmbedUrl() {
      const token = this.inbox.website_token || '';
      if (!token) return '';
      return `${this.baseHostUrl}/widget?website_token=${encodeURIComponent(token)}`;
    },
    webWidgetIframeCode() {
      if (!this.webWidgetEmbedUrl) return '';
      return `<iframe src="${this.webWidgetEmbedUrl}" title="WLAB Widget" width="400" height="600" style="border:0;"></iframe>`;
    },
    lineTokenDotClass() {
      return {
        'cfg-status-dot--green': this.lineTokenStatus === 'active_manual' || this.lineTokenStatus === 'active_auto',
        'cfg-status-dot--yellow': this.lineTokenStatus === 'expiring_soon',
        'cfg-status-dot--red': this.lineTokenStatus === 'expired',
      };
    },
  },
  watch: {
    inbox() {
      this.setDefaults();
      if (this.isGreenApiChannel) this.fetchGreenApiStatus();
    },
  },
  mounted() {
    this.setDefaults();
    if (this.isGreenApiChannel) this.fetchGreenApiStatus();
  },
  beforeUnmount() {
    this.stopGreenApiPolling();
  },
  methods: {
    setDefaults() {
      this.hmacMandatory = this.inbox.hmac_mandatory || false;
      this.liffEnabled = !!this.liffConfig.enabled;
      this.liffId = (this.liffConfig.liff_id || '').toString();
      this.liffPageUrl = (this.liffConfig.liff_page_url || '').toString();
      this.botBasicId = (this.liffConfig.bot_basic_id || '').toString();
    },
    handleHmacFlag() { this.updateInbox(); },
    async updateInbox() {
      try {
        await this.$store.dispatch('inboxes/updateInbox', {
          id: this.inbox.id, formData: false, channel: { hmac_mandatory: this.hmacMandatory },
        });
        useAlert(this.$t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
      } catch (_) {
        useAlert(this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
      }
    },
    async updateWhatsAppInboxAPIKey() {
      try {
        await this.$store.dispatch('inboxes/updateInbox', {
          id: this.inbox.id, formData: false,
          channel: { provider_config: { ...this.inbox.provider_config, api_key: this.whatsAppInboxAPIKey } },
        });
        useAlert(this.$t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
      } catch (_) {
        useAlert(this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
      }
    },
    async handleReconfigure() {
      if (this.$refs.whatsappReauth) await this.$refs.whatsappReauth.requestAuthorization();
    },
    async syncTemplates() {
      this.isSyncingTemplates = true;
      try {
        await this.$store.dispatch('inboxes/syncTemplates', this.inbox.id);
        useAlert(this.$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATES_SYNC_SUCCESS'));
      } catch (_) {
        useAlert(this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
      } finally {
        this.isSyncingTemplates = false;
      }
    },
    async refreshLineToken() {
      if (!this.isLineShortLivedMode) return;
      this.isRefreshingLineToken = true;
      try {
        await this.$store.dispatch('inboxes/refreshLineToken', this.inbox.id);
        useAlert('LINE token refreshed successfully');
        await this.$store.dispatch('inboxes/get');
      } catch (error) {
        useAlert(error.response?.data?.message || 'Failed to refresh LINE token. Please try again.');
      } finally {
        this.isRefreshingLineToken = false;
      }
    },
    validateLiffInputs({ requireId = false } = {}) {
      const id = (this.liffId || '').trim();
      if (requireId && !id) { useAlert(this.$t('INBOX_MGMT.ADD.LINE_CHANNEL.LIFF_LINKING.ERROR_LIFF_ID_REQUIRED')); return false; }
      if (id && !/^\d+-[A-Za-z0-9_-]+$/.test(id)) { useAlert(this.$t('INBOX_MGMT.ADD.LINE_CHANNEL.LIFF_LINKING.ERROR_LIFF_ID_FORMAT')); return false; }
      const url = (this.liffPageUrl || '').trim();
      if (url && !/^https?:\/\//i.test(url)) { useAlert(this.$t('INBOX_MGMT.ADD.LINE_CHANNEL.LIFF_LINKING.ERROR_LIFF_URL_FORMAT')); return false; }
      const basicId = (this.botBasicId || '').trim();
      if (basicId && !/^@/.test(basicId)) { useAlert(this.$t('INBOX_MGMT.ADD.LINE_CHANNEL.LIFF_LINKING.ERROR_BOT_BASIC_ID_FORMAT')); return false; }
      return true;
    },
    async saveLiffConfig(enabled) {
      if (!this.validateLiffInputs({ requireId: enabled })) return;
      try {
        await this.$store.dispatch('inboxes/updateInbox', {
          id: this.inbox.id, formData: false,
          channel: { liff_config: { enabled: !!enabled, liff_id: (this.liffId || '').trim(), liff_page_url: (this.liffPageUrl || '').trim(), bot_basic_id: (this.botBasicId || '').trim() } },
        });
        await this.$store.dispatch('inboxes/get');
        useAlert(this.$t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
      } catch (_) {
        useAlert(this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
      }
    },
    connectAndVerifyLiff() { return this.saveLiffConfig(true); },
    disconnectLiff() { return this.saveLiffConfig(false); },
    formatTimeAgo(date) {
      const ms = Date.now() - date.getTime();
      const mins = Math.floor(ms / 6e4);
      const hours = Math.floor(ms / 36e5);
      const days = Math.floor(ms / 864e5);
      if (mins < 60) return `${mins} minute${mins !== 1 ? 's' : ''} ago`;
      if (hours < 24) return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
      return `${days} day${days !== 1 ? 's' : ''} ago`;
    },
    async copyToClipboard(text, key) {
      try {
        await navigator.clipboard.writeText(text);
        this.copiedKey = key;
        setTimeout(() => { this.copiedKey = ''; }, 2000);
      } catch (_) {}
    },

    async fetchGreenApiStatus() {
      this.isLoadingGreenApiStatus = true;
      try {
        const { data } = await InboxesAPI.greenApiStatus(this.inbox.id);
        this.greenApiStatus = data;
      } catch (_) {
        this.greenApiStatus = null;
      } finally {
        this.isLoadingGreenApiStatus = false;
      }
    },

    async handleGreenApiReconnect() {
      this.isReconnecting = true;
      this.greenApiQrCode = null;
      this.greenApiQrCodeUrl = null;
      this.stopGreenApiPolling();
      try {
        const { data } = await InboxesAPI.greenApiReconnect(this.inbox.id);
        if (data.already_connected) {
          useAlert('Already connected. Refreshing status...');
          await this.fetchGreenApiStatus();
        } else {
          this.greenApiQrCode = data.qr_code;
          this.greenApiQrCodeUrl = data.qr_code_url;
          this.startGreenApiPolling();
        }
      } catch (e) {
        useAlert(e.response?.data?.error || 'Failed to get QR code. Please try again.');
      } finally {
        this.isReconnecting = false;
      }
    },

    startGreenApiPolling() {
      this.stopGreenApiPolling();
      this.greenApiStatusPollTimer = setInterval(async () => {
        await this.fetchGreenApiStatus();
        if (this.greenApiConnected) {
          this.greenApiQrCode = null;
          this.greenApiQrCodeUrl = null;
          this.stopGreenApiPolling();
          useAlert('WhatsApp connected successfully!');
        }
      }, 4000);
    },

    stopGreenApiPolling() {
      if (this.greenApiStatusPollTimer) {
        clearInterval(this.greenApiStatusPollTimer);
        this.greenApiStatusPollTimer = null;
      }
    },
  },
};
</script>

<template>
  <div class="cfg-page">
    <div class="cfg-body">

      <!-- ════════════ TWILIO ════════════ -->
      <template v-if="isATwilioChannel">
        <div class="cfg-header">
          <h2 class="cfg-title">Configuration</h2>
          <p class="cfg-desc">Twilio channel configuration and credentials.</p>
        </div>

        <section class="cfg-section">
          <div class="cfg-section__hd">
            <h3 class="cfg-section__title">{{ $t('INBOX_MGMT.ADD.TWILIO.API_CALLBACK.TITLE') }}</h3>
            <p class="cfg-section__desc">{{ $t('INBOX_MGMT.ADD.TWILIO.API_CALLBACK.SUBTITLE') }}</p>
          </div>
          <div class="cfg-section__bd">
            <woot-code :script="inbox.callback_webhook_url" lang="html" />
          </div>
        </section>

        <section v-if="isATwilioWhatsAppChannel" class="cfg-section">
          <div class="cfg-section__hd">
            <h3 class="cfg-section__title">{{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATES_SYNC_TITLE') }}</h3>
            <p class="cfg-section__desc">{{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATES_SYNC_SUBHEADER') }}</p>
          </div>
          <div class="cfg-section__bd">
            <button class="cfg-btn cfg-btn--primary" :disabled="isSyncingTemplates" type="button" @click="syncTemplates">
              {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATES_SYNC_BUTTON') }}
            </button>
          </div>
        </section>
      </template>

      <!-- ════════════ VOICE ════════════ -->
      <template v-else-if="isAVoiceChannel">
        <div class="cfg-header">
          <h2 class="cfg-title">Configuration</h2>
          <p class="cfg-desc">Voice channel webhook URLs.</p>
        </div>

        <section class="cfg-section">
          <div class="cfg-section__hd">
            <h3 class="cfg-section__title">{{ $t('INBOX_MGMT.ADD.VOICE.CONFIGURATION.TWILIO_VOICE_URL_TITLE') }}</h3>
            <p class="cfg-section__desc">{{ $t('INBOX_MGMT.ADD.VOICE.CONFIGURATION.TWILIO_VOICE_URL_SUBTITLE') }}</p>
          </div>
          <div class="cfg-section__bd"><woot-code :script="inbox.voice_call_webhook_url" lang="html" /></div>
        </section>

        <section class="cfg-section">
          <div class="cfg-section__hd">
            <h3 class="cfg-section__title">{{ $t('INBOX_MGMT.ADD.VOICE.CONFIGURATION.TWILIO_STATUS_URL_TITLE') }}</h3>
            <p class="cfg-section__desc">{{ $t('INBOX_MGMT.ADD.VOICE.CONFIGURATION.TWILIO_STATUS_URL_SUBTITLE') }}</p>
          </div>
          <div class="cfg-section__bd"><woot-code :script="inbox.voice_status_webhook_url" lang="html" /></div>
        </section>
      </template>

      <!-- ════════════ LINE ════════════ -->
      <template v-else-if="isALineChannel">
        <div class="cfg-header">
          <h2 class="cfg-title">Configuration</h2>
          <p class="cfg-desc">LINE channel settings, token status and LIFF linking.</p>
        </div>

        <!-- Token status -->
        <section class="cfg-section">
          <div class="cfg-section__hd">
            <h3 class="cfg-section__title">{{ $t('INBOX_MGMT.ADD.LINE_CHANNEL.TOKEN_STATUS.TITLE') }}</h3>
            <p class="cfg-section__desc">{{ lineTokenStatusSubtitle }}</p>
          </div>
          <div class="cfg-section__bd">
            <div class="cfg-status-card">
              <div class="cfg-status-card__left">
                <span class="cfg-status-dot" :class="lineTokenDotClass" />
                <div>
                  <p class="cfg-status-card__title">{{ lineTokenStatusText }}</p>
                  <p v-if="lineTokenExpiresIn" class="cfg-status-card__sub">{{ lineTokenExpiresIn }}</p>
                  <p v-if="lineTokenLastRefreshed" class="cfg-status-card__sub">{{ lineTokenLastRefreshed }}</p>
                </div>
              </div>
              <button
                v-if="isLineShortLivedMode"
                class="cfg-btn cfg-btn--outline"
                type="button"
                :disabled="isRefreshingLineToken"
                @click="refreshLineToken"
              >
                {{ $t('INBOX_MGMT.ADD.LINE_CHANNEL.TOKEN_STATUS.REFRESH_NOW') }}
              </button>
            </div>
          </div>
        </section>

        <!-- Webhook -->
        <section class="cfg-section">
          <div class="cfg-section__hd">
            <h3 class="cfg-section__title">{{ $t('INBOX_MGMT.ADD.LINE_CHANNEL.API_CALLBACK.TITLE') }}</h3>
            <p class="cfg-section__desc">{{ $t('INBOX_MGMT.ADD.LINE_CHANNEL.API_CALLBACK.SUBTITLE') }}</p>
          </div>
          <div class="cfg-section__bd"><woot-code :script="inbox.callback_webhook_url" lang="html" /></div>
        </section>

        <!-- LIFF -->
        <section class="cfg-section">
          <div class="cfg-section__hd">
            <h3 class="cfg-section__title">{{ $t('INBOX_MGMT.ADD.LINE_CHANNEL.LIFF_LINKING.TITLE') }}</h3>
            <p class="cfg-section__desc">{{ $t('INBOX_MGMT.ADD.LINE_CHANNEL.LIFF_LINKING.SUBTITLE') }}</p>
          </div>
          <div class="cfg-section__bd">
            <!-- Status row -->
            <div class="cfg-status-card">
              <div class="cfg-status-card__left">
                <span class="cfg-status-dot" :class="liffEnabled ? 'cfg-status-dot--green' : 'cfg-status-dot--grey'" />
                <div>
                  <p class="cfg-status-card__title">{{ liffEnabledText }}</p>
                  <p class="cfg-status-card__sub">{{ $t('INBOX_MGMT.ADD.LINE_CHANNEL.LIFF_LINKING.HELP') }}</p>
                </div>
              </div>
              <div class="cfg-btn-group">
                <button class="cfg-btn cfg-btn--outline" type="button" @click="connectAndVerifyLiff">
                  {{ $t('INBOX_MGMT.ADD.LINE_CHANNEL.LIFF_LINKING.CONNECT') }}
                </button>
                <button class="cfg-btn cfg-btn--outline" type="button" @click="disconnectLiff">
                  {{ $t('INBOX_MGMT.ADD.LINE_CHANNEL.LIFF_LINKING.DISCONNECT') }}
                </button>
              </div>
            </div>

            <!-- Inputs -->
            <div class="cfg-form-grid">
              <div class="cfg-field">
                <label class="cfg-label">{{ $t('INBOX_MGMT.ADD.LINE_CHANNEL.LIFF_LINKING.LIFF_ID_LABEL') }}</label>
                <input v-model="liffId" type="text" class="cfg-input" :placeholder="$t('INBOX_MGMT.ADD.LINE_CHANNEL.LIFF_LINKING.LIFF_ID_PLACEHOLDER')" />
              </div>
              <div class="cfg-field">
                <label class="cfg-label">
                  {{ $t('INBOX_MGMT.ADD.LINE_CHANNEL.LIFF_LINKING.LIFF_PAGE_URL_LABEL') }}
                  <span class="cfg-optional">({{ $t('INBOX_MGMT.ADD.LINE_CHANNEL.LIFF_LINKING.OPTIONAL') }})</span>
                </label>
                <input v-model="liffPageUrl" type="text" class="cfg-input" placeholder="https://..." />
              </div>
              <div class="cfg-field">
                <label class="cfg-label">
                  {{ $t('INBOX_MGMT.ADD.LINE_CHANNEL.LIFF_LINKING.BOT_BASIC_ID_LABEL') }}
                  <span class="cfg-optional">({{ $t('INBOX_MGMT.ADD.LINE_CHANNEL.LIFF_LINKING.OPTIONAL') }})</span>
                </label>
                <input v-model="botBasicId" type="text" class="cfg-input" placeholder="@your_oa" />
              </div>
            </div>

            <!-- Check-in URL -->
            <div class="cfg-field cfg-field--mt">
              <label class="cfg-label">{{ $t('INBOX_MGMT.ADD.LINE_CHANNEL.LIFF_LINKING.CHECKIN_URL_TITLE') }}</label>
              <woot-code :script="liffCheckinUrl" lang="html" />
              <p class="cfg-hint">{{ $t('INBOX_MGMT.ADD.LINE_CHANNEL.LIFF_LINKING.CHECKIN_URL_HELP') }}</p>
            </div>
          </div>
        </section>
      </template>

      <!-- ════════════ WEB WIDGET ════════════ -->
      <template v-else-if="isAWebWidgetInbox">
        <div class="cfg-header">
          <h2 class="cfg-title">Configuration</h2>
          <p class="cfg-desc">Embed your live chat widget on your website.</p>
        </div>

        <!-- Messenger script -->
        <section class="cfg-section">
          <div class="cfg-section__hd">
            <h3 class="cfg-section__title">{{ $t('INBOX_MGMT.SETTINGS_POPUP.MESSENGER_HEADING') }}</h3>
            <p class="cfg-section__desc">{{ $t('INBOX_MGMT.SETTINGS_POPUP.MESSENGER_SUB_HEAD') }}</p>
          </div>
          <div class="cfg-section__bd">
            <woot-code :script="inbox.web_widget_script" lang="html" :codepen-title="`${inbox.name} - WLAB Widget Test`" enable-code-pen />
          </div>
        </section>

        <!-- Embed URL -->
        <section v-if="webWidgetEmbedUrl" class="cfg-section">
          <div class="cfg-section__hd">
            <h3 class="cfg-section__title">{{ $t('INBOX_MGMT.SETTINGS_POPUP.MESSENGER_URL_HEADING') }}</h3>
            <p class="cfg-section__desc">{{ $t('INBOX_MGMT.SETTINGS_POPUP.MESSENGER_URL_SUB_HEAD') }}</p>
          </div>
          <div class="cfg-section__bd"><woot-code :script="webWidgetEmbedUrl" lang="html" /></div>
        </section>

        <!-- iFrame code -->
        <section v-if="webWidgetIframeCode" class="cfg-section">
          <div class="cfg-section__hd">
            <h3 class="cfg-section__title">{{ $t('INBOX_MGMT.SETTINGS_POPUP.MESSENGER_IFRAME_HEADING') }}</h3>
            <p class="cfg-section__desc">{{ $t('INBOX_MGMT.SETTINGS_POPUP.MESSENGER_IFRAME_SUB_HEAD') }}</p>
          </div>
          <div class="cfg-section__bd"><woot-code :script="webWidgetIframeCode" lang="html" /></div>
        </section>

        <!-- HMAC token -->
        <section class="cfg-section">
          <div class="cfg-section__hd">
            <h3 class="cfg-section__title">{{ $t('INBOX_MGMT.SETTINGS_POPUP.HMAC_VERIFICATION') }}</h3>
            <p class="cfg-section__desc">
              {{ $t('INBOX_MGMT.SETTINGS_POPUP.HMAC_DESCRIPTION') }}
              <a class="cfg-link" target="_blank" rel="noopener noreferrer" href="https://wlab.biz/docs/product/channels/live-chat/sdk/identity-validation/">
                {{ $t('INBOX_MGMT.SETTINGS_POPUP.HMAC_LINK_TO_DOCS') }}
              </a>
            </p>
          </div>
          <div class="cfg-section__bd"><woot-code :script="inbox.hmac_token" /></div>
        </section>

        <!-- HMAC mandatory -->
        <section class="cfg-section">
          <div class="cfg-section__hd">
            <h3 class="cfg-section__title">{{ $t('INBOX_MGMT.SETTINGS_POPUP.HMAC_MANDATORY_VERIFICATION') }}</h3>
            <p class="cfg-section__desc">{{ $t('INBOX_MGMT.SETTINGS_POPUP.HMAC_MANDATORY_DESCRIPTION') }}</p>
          </div>
          <div class="cfg-section__bd">
            <label class="cfg-toggle-row">
              <span class="cfg-toggle-wrap">
                <input id="hmacMandatory" v-model="hmacMandatory" type="checkbox" class="cfg-checkbox-input" @change="handleHmacFlag" />
                <span class="cfg-toggle-track" :class="{ 'cfg-toggle-track--on': hmacMandatory }">
                  <span class="cfg-toggle-knob" />
                </span>
              </span>
              <span class="cfg-toggle-text">{{ $t('INBOX_MGMT.EDIT.ENABLE_HMAC.LABEL') }}</span>
            </label>
          </div>
        </section>
      </template>

      <!-- ════════════ API INBOX ════════════ -->
      <template v-else-if="isAPIInbox">
        <div class="cfg-header">
          <h2 class="cfg-title">Configuration</h2>
          <p class="cfg-desc">API channel credentials and identity validation.</p>
        </div>

        <section class="cfg-section">
          <div class="cfg-section__hd">
            <h3 class="cfg-section__title">{{ $t('INBOX_MGMT.SETTINGS_POPUP.INBOX_IDENTIFIER') }}</h3>
            <p class="cfg-section__desc">{{ $t('INBOX_MGMT.SETTINGS_POPUP.INBOX_IDENTIFIER_SUB_TEXT') }}</p>
          </div>
          <div class="cfg-section__bd"><woot-code :script="inbox.inbox_identifier" /></div>
        </section>

        <section class="cfg-section">
          <div class="cfg-section__hd">
            <h3 class="cfg-section__title">{{ $t('INBOX_MGMT.SETTINGS_POPUP.HMAC_VERIFICATION') }}</h3>
            <p class="cfg-section__desc">{{ $t('INBOX_MGMT.SETTINGS_POPUP.HMAC_DESCRIPTION') }}</p>
          </div>
          <div class="cfg-section__bd"><woot-code :script="inbox.hmac_token" /></div>
        </section>

        <section class="cfg-section">
          <div class="cfg-section__hd">
            <h3 class="cfg-section__title">{{ $t('INBOX_MGMT.SETTINGS_POPUP.HMAC_MANDATORY_VERIFICATION') }}</h3>
            <p class="cfg-section__desc">{{ $t('INBOX_MGMT.SETTINGS_POPUP.HMAC_MANDATORY_DESCRIPTION') }}</p>
          </div>
          <div class="cfg-section__bd">
            <label class="cfg-toggle-row">
              <span class="cfg-toggle-wrap">
                <input id="hmacMandatory" v-model="hmacMandatory" type="checkbox" class="cfg-checkbox-input" @change="handleHmacFlag" />
                <span class="cfg-toggle-track" :class="{ 'cfg-toggle-track--on': hmacMandatory }">
                  <span class="cfg-toggle-knob" />
                </span>
              </span>
              <span class="cfg-toggle-text">{{ $t('INBOX_MGMT.EDIT.ENABLE_HMAC.LABEL') }}</span>
            </label>
          </div>
        </section>
      </template>

      <!-- ════════════ EMAIL ════════════ -->
      <template v-else-if="isAnEmailChannel">
        <div class="cfg-header">
          <h2 class="cfg-title">Configuration</h2>
          <p class="cfg-desc">Email forwarding, IMAP and SMTP settings.</p>
        </div>

        <section class="cfg-section">
          <div class="cfg-section__hd">
            <h3 class="cfg-section__title">{{ $t('INBOX_MGMT.SETTINGS_POPUP.FORWARD_EMAIL_TITLE') }}</h3>
            <p class="cfg-section__desc">{{ $t('INBOX_MGMT.SETTINGS_POPUP.FORWARD_EMAIL_SUB_TEXT') }}</p>
          </div>
          <div class="cfg-section__bd"><woot-code :script="inbox.forward_to_email" /></div>
        </section>

        <ImapSettings :inbox="inbox" />
        <SmtpSettings v-if="inbox.imap_enabled" :inbox="inbox" />
      </template>

      <!-- ════════════ WHATSAPP (Green API) ════════════ -->
      <template v-else-if="isGreenApiChannel">
        <div class="cfg-header">
          <h2 class="cfg-title">Configuration</h2>
          <p class="cfg-desc">WhatsApp connection status and reconnect.</p>
        </div>

        <!-- Connection status -->
        <section class="cfg-section">
          <div class="cfg-section__hd">
            <h3 class="cfg-section__title">Connection Status</h3>
            <p class="cfg-section__desc">Current WhatsApp connection state for this inbox.</p>
          </div>
          <div class="cfg-section__bd">
            <div class="cfg-status-card">
              <div class="cfg-status-card__left">
                <span class="cfg-status-dot" :class="greenApiStatusDotClass" />
                <div>
                  <p class="cfg-status-card__title">{{ greenApiStatusText }}</p>
                  <p v-if="greenApiConnected && greenApiPhoneNumber !== '—'" class="cfg-status-card__sub">{{ greenApiPhoneNumber }}</p>
                  <p v-if="greenApiConnected && greenApiLastAuthorized !== '—'" class="cfg-status-card__sub">Last connected: {{ greenApiLastAuthorized }}</p>
                </div>
              </div>
              <button
                class="cfg-btn cfg-btn--outline"
                type="button"
                :disabled="isReconnecting || isLoadingGreenApiStatus"
                @click="handleGreenApiReconnect"
              >
                {{ isReconnecting ? 'Getting QR...' : 'Reconnect' }}
              </button>
            </div>

            <!-- QR code -->
            <div v-if="greenApiQrCode || greenApiQrCodeUrl" class="cfg-qr-block">
              <p class="cfg-qr-block__label">Scan with WhatsApp to connect</p>
              <img
                v-if="greenApiQrCodeUrl"
                :src="greenApiQrCodeUrl"
                class="cfg-qr-img"
                alt="WhatsApp QR Code"
              />
              <img
                v-else-if="greenApiQrCode"
                :src="`data:image/png;base64,${greenApiQrCode}`"
                class="cfg-qr-img"
                alt="WhatsApp QR Code"
              />
              <p class="cfg-hint">Open WhatsApp → Linked Devices → Link a Device → Scan this QR code</p>
            </div>
          </div>
        </section>

        <!-- Instance details -->
        <section class="cfg-section">
          <div class="cfg-section__hd">
            <h3 class="cfg-section__title">Instance Details</h3>
            <p class="cfg-section__desc">WhatsApp instance information for this inbox.</p>
          </div>
          <div class="cfg-section__bd">
            <div class="cfg-info-grid">
              <div class="cfg-info-row">
                <span class="cfg-info-row__label">Phone Number</span>
                <span class="cfg-info-row__value">{{ greenApiPhoneNumber }}</span>
              </div>
              <div class="cfg-info-row">
                <span class="cfg-info-row__label">Instance ID</span>
                <span class="cfg-info-row__value">{{ greenApiInstanceId }}</span>
              </div>
              <div class="cfg-info-row">
                <span class="cfg-info-row__label">Last Connected</span>
                <span class="cfg-info-row__value">{{ greenApiLastAuthorized }}</span>
              </div>
            </div>
          </div>
        </section>
      </template>

      <!-- ════════════ WHATSAPP (non-Twilio) ════════════ -->
      <template v-else-if="isAWhatsAppChannel && !isATwilioChannel">
        <div class="cfg-header">
          <h2 class="cfg-title">Configuration</h2>
          <p class="cfg-desc">WhatsApp channel credentials and template sync.</p>
        </div>

        <template v-if="inbox.provider_config">
          <!-- Embedded signup -->
          <template v-if="isEmbeddedSignupWhatsApp">
            <section v-if="whatsappAppId" class="cfg-section">
              <div class="cfg-section__hd">
                <h3 class="cfg-section__title">{{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_EMBEDDED_SIGNUP_TITLE') }}</h3>
                <p class="cfg-section__desc">{{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_EMBEDDED_SIGNUP_SUBHEADER') }}</p>
              </div>
              <div class="cfg-section__bd">
                <p class="cfg-hint">{{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_EMBEDDED_SIGNUP_DESCRIPTION') }}</p>
                <button class="cfg-btn cfg-btn--primary" type="button" @click="handleReconfigure">
                  {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_RECONFIGURE_BUTTON') }}
                </button>
              </div>
            </section>
          </template>

          <!-- Manual setup -->
          <template v-else>
            <section class="cfg-section">
              <div class="cfg-section__hd">
                <h3 class="cfg-section__title">{{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_WEBHOOK_TITLE') }}</h3>
                <p class="cfg-section__desc">{{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_WEBHOOK_SUBHEADER') }}</p>
              </div>
              <div class="cfg-section__bd"><woot-code :script="inbox.provider_config.webhook_verify_token" /></div>
            </section>

            <section class="cfg-section">
              <div class="cfg-section__hd">
                <h3 class="cfg-section__title">{{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_TITLE') }}</h3>
                <p class="cfg-section__desc">{{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_SUBHEADER') }}</p>
              </div>
              <div class="cfg-section__bd"><woot-code :script="inbox.provider_config.api_key" /></div>
            </section>

            <section class="cfg-section">
              <div class="cfg-section__hd">
                <h3 class="cfg-section__title">{{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_UPDATE_TITLE') }}</h3>
                <p class="cfg-section__desc">{{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_UPDATE_SUBHEADER') }}</p>
              </div>
              <div class="cfg-section__bd">
                <div class="cfg-inline-form">
                  <input
                    v-model="whatsAppInboxAPIKey"
                    type="text"
                    class="cfg-input cfg-input--flex"
                    :placeholder="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_UPDATE_PLACEHOLDER')"
                  />
                  <button
                    class="cfg-btn cfg-btn--primary"
                    type="button"
                    :disabled="v$.whatsAppInboxAPIKey.$invalid"
                    @click="updateWhatsAppInboxAPIKey"
                  >
                    {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_UPDATE_BUTTON') }}
                  </button>
                </div>
              </div>
            </section>
          </template>

          <section class="cfg-section">
            <div class="cfg-section__hd">
              <h3 class="cfg-section__title">{{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATES_SYNC_TITLE') }}</h3>
              <p class="cfg-section__desc">{{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATES_SYNC_SUBHEADER') }}</p>
            </div>
            <div class="cfg-section__bd">
              <button class="cfg-btn cfg-btn--primary" type="button" :disabled="isSyncingTemplates" @click="syncTemplates">
                {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATES_SYNC_BUTTON') }}
              </button>
            </div>
          </section>
        </template>

        <WhatsappReauthorize v-if="isEmbeddedSignupWhatsApp" ref="whatsappReauth" :inbox="inbox" class="hidden" />
      </template>

    </div>
  </div>
</template>

<style scoped>
/* ── Page shell ── */
.cfg-page {
  height: 100%;
  overflow-y: auto;
  background: #ffffff;
}

.cfg-body {
  max-width: 720px;
  margin: 0;
  padding: 32px 32px 80px;
  display: flex;
  flex-direction: column;
  gap: 0;
}

/* ── Header ── */
.cfg-header {
  padding-bottom: 24px;
  border-bottom: 1px solid #e5e7eb;
  margin-bottom: 0;
}

.cfg-title {
  font-size: 12px;
  font-weight: 700;
  color: #111827;
  margin: 0 0 6px;
}

.cfg-desc {
  font-size: 11px;
  color: rgba(0, 0, 0, 0.55);
  margin: 0;
  line-height: 1.5;
}

/* ── Section ── */
.cfg-section {
  padding: 24px 0;
  border-bottom: 1px solid #e5e7eb;
}

.cfg-section:last-child {
  border-bottom: none;
}

.cfg-section__hd {
  margin-bottom: 14px;
}

.cfg-section__title {
  font-size: 14px;
  font-weight: 700;
  color: #111827;
  margin: 0 0 4px;
}

.cfg-section__desc {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.5);
  margin: 0;
  line-height: 1.55;
}

.cfg-section__bd {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

/* ── Code blocks ── */
.cfg-section__bd :deep(code.hljs),
.cfg-section__bd :deep(.hljs) {
  background: #f3f4f6 !important;
  color: #111827 !important;
  border: 1px solid #e5e7eb;
  border-radius: 10px !important;
}

.cfg-section__bd :deep(.hljs-name),
.cfg-section__bd :deep(.hljs-tag),
.cfg-section__bd :deep(.hljs-attr),
.cfg-section__bd :deep(.hljs-attribute) {
  color: #6b7280 !important;
}

/* ── Buttons ── */
.cfg-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 9px 20px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid transparent;
  transition: background 0.15s, border-color 0.15s, opacity 0.15s;
}

.cfg-btn--primary {
  background: #111827;
  color: #ffffff;
  border-color: #111827;
}

.cfg-btn--primary:hover:not(:disabled) {
  background: #1f2937;
}

.cfg-btn--outline {
  background: #fff;
  color: #374151;
  border-color: #e5e7eb;
}

.cfg-btn--outline:hover:not(:disabled) {
  background: #f9fafb;
  border-color: #d1d5db;
}

.cfg-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.cfg-btn-group {
  display: flex;
  gap: 8px;
}

/* ── Status card ── */
.cfg-status-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 16px 18px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #fff;
}

.cfg-status-card__left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.cfg-status-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
  background: #d1d5db;
}

.cfg-status-dot--green  { background: #22c55e; }
.cfg-status-dot--yellow { background: #f59e0b; }
.cfg-status-dot--red    { background: #ef4444; }
.cfg-status-dot--grey   { background: #9ca3af; }

.cfg-status-card__title {
  font-size: 13px;
  font-weight: 600;
  color: #111827;
  margin: 0 0 2px;
}

.cfg-status-card__sub {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.5);
  margin: 0;
}

/* ── Toggle ── */
.cfg-toggle-row {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  user-select: none;
}

.cfg-toggle-wrap { position: relative; flex-shrink: 0; }

.cfg-checkbox-input {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
}

.cfg-toggle-track {
  display: flex;
  align-items: center;
  width: 38px;
  height: 22px;
  border-radius: 11px;
  background: #d1d5db;
  padding: 2px;
  transition: background 0.2s;
  cursor: pointer;
}

.cfg-toggle-track--on { background: #4384f5; }

.cfg-toggle-knob {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #ffffff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  transition: transform 0.2s;
}

.cfg-toggle-track--on .cfg-toggle-knob { transform: translateX(16px); }

.cfg-toggle-text {
  font-size: 13px;
  font-weight: 500;
  color: #111827;
}

/* ── Form ── */
.cfg-form-grid {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.cfg-inline-form {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.cfg-field {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.cfg-field--mt { margin-top: 8px; }

.cfg-label {
  font-size: 12px;
  font-weight: 600;
  color: #111827;
}

.cfg-optional {
  font-weight: 400;
  color: rgba(0, 0, 0, 0.45);
}

.cfg-input {
  width: 100%;
  max-width: 400px;
  padding: 9px 13px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 13px;
  color: #111827;
  background: #ffffff;
  outline: none;
  transition: border-color 0.15s;
}

.cfg-input--flex { max-width: none; flex: 1; min-width: 0; }

.cfg-input:focus {
  border-color: #4384f5;
  box-shadow: 0 0 0 3px rgba(67, 132, 245, 0.12);
}

.cfg-hint {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.5);
  margin: 0;
  line-height: 1.5;
}

.cfg-link {
  color: #4384f5;
  text-decoration: none;
}

.cfg-link:hover { text-decoration: underline; }

/* ── QR code block ── */
.cfg-qr-block {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
  padding: 18px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #fafafa;
}

.cfg-qr-block__label {
  font-size: 13px;
  font-weight: 600;
  color: #111827;
  margin: 0;
}

.cfg-qr-img {
  width: 200px;
  height: 200px;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  object-fit: contain;
  background: #fff;
}

/* ── Info grid ── */
.cfg-info-grid {
  display: flex;
  flex-direction: column;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  overflow: hidden;
}

.cfg-info-row {
  display: flex;
  align-items: center;
  padding: 11px 16px;
  border-bottom: 1px solid #e5e7eb;
  gap: 12px;
}

.cfg-info-row:last-child {
  border-bottom: none;
}

.cfg-info-row__label {
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
  min-width: 140px;
  flex-shrink: 0;
}

.cfg-info-row__value {
  font-size: 13px;
  color: #111827;
  font-family: ui-monospace, monospace;
}
</style>
